// Core React & WordPress Element imports
import React, { createContext, StrictMode } from 'react'; // Import createContext & StrictMode from React
import { createRoot } from 'react-dom/client'; // Use the modern createRoot for the primary render attempt

// Application specific imports
import App from './App';
import { setConfig } from './configStore';
import ErrorComponent from './ErrorComponent';
import './style/main.scss'; // Styles side effect

// Library imports
import { createBrowserRouter, RouterProvider } from 'react-router-dom'; // Only necessary router imports
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// --- Constants ---
const ROOT_SELECTOR_PREFIX = 'range-headless-root-';
const DATASET_ATTRIBUTE = 'rangeheadless';
const LOCAL_STORAGE_ERROR_KEY = 'range-react-error';
const FALLBACK_ENABLED_SHORTCODES = ['headlessmenu', 'headlessproducts'];
const DEFAULT_ALTERNATE_MENU_URL = '/backup-menu/';

// --- Contexts ---
export const ConfigContext = createContext(null);
export const ShortcodeContext = createContext(null);

// --- Helper Functions ---

/**
 * Checks if a shortcode identified by its data should display a fallback UI.
 * @param {object | null} shortcodeData - Parsed data from the shortcode's dataset.
 * @returns {boolean} - True if fallback should be shown, false otherwise.
 */
function shouldShowFallback(shortcodeData) {
    return (
        shortcodeData &&
        shortcodeData.shortcode &&
        FALLBACK_ENABLED_SHORTCODES.includes(shortcodeData.shortcode)
    );
}

/**
 * Generates the HTML markup for the fallback message.
 * @param {string} [alternateMenuUrl=DEFAULT_ALTERNATE_MENU_URL] - The URL for the fallback link.
 * @returns {string} - HTML string for the fallback UI.
 */
function createFallbackHtml(alternateMenuUrl = DEFAULT_ALTERNATE_MENU_URL) {
    // Using inline styles for self-contained fallback UI that doesn't rely on external CSS loading.
    return `
        <div class="headless-failsafe" style="text-align: center; padding: 20px; margin: 20px; border: 1px solid #ddd; border-radius: 5px; background-color: #f9f9f9; position:relative;z-index:9999;">
            <h3 style="color: #333; margin-bottom: 15px;">Not seeing our menu?</h3>
            <a target="_blank" href="${alternateMenuUrl}" style="display: inline-block; padding: 10px 20px; background-color: #4CAF50; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">Click Here</a>
        </div>
    `;
}

/**
 * Attempts to render the React application using various available methods.
 * It prioritizes modern React, then legacy React, then WordPress-provided React APIs.
 * Relies on globally available `React`, `ReactDOM`, and `wp.element` for maximum compatibility
 * in potentially inconsistent WordPress environments where script loading might fail.
 * @param {Element} rootElement - The DOM element to render into.
 * @param {React.ReactNode} appComponent - The React component to render.
 * @returns {boolean} - True if rendering succeeded with any method, false otherwise.
 */
function attemptRender(rootElement, appComponent) {
    // Check if React and ReactDOM are globally available (robust check for WP environments)
    const hasReact = typeof React !== 'undefined';
    const hasReactDOM = typeof ReactDOM !== 'undefined';
    const hasWpElement = typeof wp !== 'undefined' && wp.element;

    // 1. Try modern React API (createRoot)
    if (hasReactDOM && typeof ReactDOM.createRoot === 'function') {
        try {
            console.log('Attempting render with ReactDOM.createRoot');
            ReactDOM.createRoot(rootElement).render(appComponent);
            return true;
        } catch (error) {
            console.warn('ReactDOM.createRoot failed:', error);
        }
    }

    // 2. Try legacy React API (render)
    if (hasReactDOM && typeof ReactDOM.render === 'function') {
        try {
            console.log('Attempting render with ReactDOM.render');
            ReactDOM.render(appComponent, rootElement);
            return true;
        } catch (error) {
            console.warn('ReactDOM.render failed:', error);
        }
    }

    // 3. Try modern WordPress Element API (createRoot)
    if (hasWpElement && typeof wp.element.createRoot === 'function') {
        try {
            console.log('Attempting render with wp.element.createRoot');
            wp.element.createRoot(rootElement).render(appComponent);
            return true;
        } catch (error) {
            console.warn('wp.element.createRoot failed:', error);
        }
    }

    // 4. Try legacy WordPress Element API (render)
    if (hasWpElement && typeof wp.element.render === 'function') {
        try {
            console.log('Attempting render with wp.element.render');
            wp.element.render(appComponent, rootElement);
            return true;
        } catch (error) {
            console.warn('wp.element.render failed:', error);
        }
    }

    console.error('All React rendering methods failed - no compatible API found.');
    return false;
}

/**
 * Applies the fallback HTML to eligible root elements.
 * @param {NodeListOf<Element>} roots - The collection of root DOM elements.
 * @param {object} shortcodeData - An object mapping root IDs to their parsed data.
 * @param {object | null} globalConfig - The parsed global configuration object.
 */
function applyFallbackHtmlToRoots(roots, shortcodeData, globalConfig) {
    let fallbackCount = 0;
    const alternateMenuUrl = globalConfig?.alternateMenuUrl || DEFAULT_ALTERNATE_MENU_URL;

    roots.forEach((rootElement) => {
        try {
            const rootId = rootElement.id;
            // shortcodeData might be incomplete if parsing failed earlier
            const data = shortcodeData ? shortcodeData[rootId] : null;

            // Attempt to parse data again if it wasn't available (e.g., during catastrophic error)
            let currentData = data;
            if (!currentData && rootElement.dataset[DATASET_ATTRIBUTE]) {
                 try {
                     currentData = JSON.parse(rootElement.dataset[DATASET_ATTRIBUTE]);
                 } catch (parseError) {
                     console.warn(`Failed to parse fallback data for ${rootId}:`, parseError);
                 }
            }


            if (shouldShowFallback(currentData)) {
                rootElement.innerHTML = createFallbackHtml(alternateMenuUrl);
                fallbackCount++;
            }
        } catch (error) {
            console.error(`Error applying fallback to root ${rootElement.id}:`, error);
        }
    });

    if (fallbackCount > 0) {
        console.log(`Displayed fallback message for ${fallbackCount} eligible shortcodes.`);
        // Record the failure state
        try {
            localStorage.setItem(LOCAL_STORAGE_ERROR_KEY, 'true');
        } catch (e) {
            console.warn('Failed to set localStorage error flag:', e);
        }
    }
}

/**
 * Main initialization function for the React application within WordPress.
 */
function initializeApp() {
    let globalConfig = null;
    const shortcodeData = {};
    let roots = [];

    try {
        // 1. Pre-Initialization Cleanup (Optional Error Recovery)
        try {
            if (localStorage.getItem(LOCAL_STORAGE_ERROR_KEY)) {
                console.log('Previous React errors detected, clearing potentially problematic cache data.');
                Object.keys(localStorage).forEach(key => {
                    // Be specific about keys to avoid clearing unrelated data
                    if (key.startsWith("headless_") || key.includes("react") || key.includes("DUTCHIE")) {
                        localStorage.removeItem(key);
                    }
                });
                // Optional: Remove the flag itself after clearing
                // localStorage.removeItem(LOCAL_STORAGE_ERROR_KEY);
            }
        } catch (e) {
            console.warn('Could not access or clear localStorage:', e);
        }

        // 2. Find Root Elements
        roots = document.querySelectorAll(`[id^="${ROOT_SELECTOR_PREFIX}"]`);
        if (roots.length === 0) {
            console.warn('No Range Headless root elements found. App initialization stopped.');
            return;
        }

        // 3. Collect Data and Configuration
        roots.forEach((root) => {
            const rootId = root.id;
            try {
                const rawData = root.dataset[DATASET_ATTRIBUTE];
                if (!rawData) {
                    throw new Error(`No data attribute found for root ${rootId}`);
                }
                const parsedData = JSON.parse(rawData);
                shortcodeData[rootId] = parsedData;

                // Capture global config if present
                if (parsedData.hasGlobal && parsedData.globalConfig) {
                    if (globalConfig) {
                         console.warn(`Multiple global configs found. Using the one from ${rootId}.`);
                    }
                    globalConfig = parsedData.globalConfig;
                }
            } catch (error) {
                console.error(`Failed to process data for root ${rootId}:`, error);
                // Continue processing other roots
            }
        });

        // 4. Finalize Global Config (if not found directly)
        if (!globalConfig && Object.keys(shortcodeData).length > 0) {
            const firstRootData = Object.values(shortcodeData)[0];
            if (firstRootData?.globalConfig) {
                console.log('Using global config from the first processed shortcode.');
                globalConfig = firstRootData.globalConfig;
            }
        }

        if (!globalConfig) {
            console.error('No global config could be determined. App initialization might fail or use defaults.');
            // Decide if you should return here or proceed with defaults
            // return;
        } else {
            setConfig(globalConfig); // Set config in the store
        }


        // 5. Setup React Query
        const queryClient = new QueryClient({
            defaultOptions: {
                queries: {
                    retry: (failureCount, error) => {
                        if (error?.response?.status === 429) return false; // No retry on 429
                        return failureCount < 2; // Retry twice otherwise
                    },
                    staleTime: 1 * 60 * 1000, // 1 minute
                    cacheTime: 30 * 60 * 1000, // 30 minutes
                    refetchOnWindowFocus: false,
                    refetchOnReconnect: false,
                },
            },
        });

        // 6. Setup React Router
        // Note: Assumes App component can handle dynamic segments appropriately
        const router = createBrowserRouter([
            {
                // This path structure implies your App component handles these optional params
                path: '/:viewName?/:locationSlug?/:categoryType?/:categoryName?/:specialCategoryName?',
                element: (
                    <Sentry.ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                        <ConfigContext.Provider value={globalConfig}>
                            <ShortcodeContext.Provider value={shortcodeData}>
                                {/* Pass necessary data down explicitly or rely solely on context */}
                                <App
                                    /* You might not need to pass these if App uses the contexts */
                                    shortcodeData={shortcodeData}
                                    globalConfig={globalConfig}
                                    fallback={rangeHeadlessApi.fallback} /* Ensure rangeHeadlessApi is defined globally or passed */
                                />
                            </ShortcodeContext.Provider>
                        </ConfigContext.Provider>
                    </Sentry.ErrorBoundary>
                ),
            },
            // Add other routes if needed
        ]);

        // 7. Render the Application
        // IMPORTANT: This renders the main RouterProvider app ONLY into the FIRST root element found.
        // Other root elements might just be data sources or targets for fallbacks.
        const primaryRootElement = roots[0];
        const appComponent = (
            <StrictMode>
                <QueryClientProvider client={queryClient}>
                    <RouterProvider router={router} />
                </QueryClientProvider>
            </StrictMode>
        );

        // Use the explicit modern React API first for the primary path
        let renderSuccess = false;
        try {
            console.log('Attempting primary render with React 18 createRoot');
            const reactRoot = createRoot(primaryRootElement);
            reactRoot.render(appComponent);
            renderSuccess = true;
             // Clear error flag on successful render
            try {
                localStorage.removeItem(LOCAL_STORAGE_ERROR_KEY);
            } catch (e) { /* ignore */ }
        } catch (initialRenderError) {
            console.warn('Primary React 18 createRoot render failed, trying fallbacks:', initialRenderError);
            // If the primary method fails, try the sequence of fallbacks
            renderSuccess = attemptRender(primaryRootElement, appComponent);
        }


        // 8. Apply Fallback UI if Rendering Failed
        if (!renderSuccess) {
            console.error('React application rendering failed.');
            applyFallbackHtmlToRoots(roots, shortcodeData, globalConfig);
        }

    } catch (error) {
        // --- Catastrophic Error Handling ---
        console.error('Catastrophic failure during Range Headless initialization:', error);
        // Attempt to apply fallbacks even if initialization failed midway
        applyFallbackHtmlToRoots(roots, shortcodeData, globalConfig); // Pass potentially incomplete data

        // Ensure the error flag is set in localStorage on catastrophic failure
         try {
            localStorage.setItem(LOCAL_STORAGE_ERROR_KEY, 'true');
        } catch (e) {
             console.warn('Failed to set localStorage error flag during catastrophic error:', e);
        }
    }
}

// --- Initialize ---
// Ensures DOM is ready before querying elements, especially in WP environments.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp);
} else {
    initializeApp(); // DOMContentLoaded has already fired
}
